import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Package,
  DollarSign,
  Hash,
  TrendingUp,
  Box,
  Edit2,
  Trash2,
  Globe,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import DeleteAlertModal from "../../components/common/DeleteAlertModal";

const SupplierProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState("en");

  // Helper: extract text based on active language
  const getText = (field) => {
    if (!field) return "";
    if (typeof field === "string") return field;
    if (typeof field === "object") {
      // If it's a multilingual object ({en, hi, es, zh})
      if (field[activeLanguage]) return field[activeLanguage];
      // Fallback to English or first available
      return field.en || field.hi || field.es || field.zh || Object.values(field)[0] || "";
    }
    return "";
  };

  // Helper: extract English text for category/subcategory names (they are also multilingual objects)
  const getEnglishText = (field) => {
    if (!field) return "";
    if (typeof field === "string") return field;
    if (typeof field === "object") return field.en || "";
    return "";
  };

  // Fetch Product Details
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/products/get-product-by-id/${id}`
        );
        const fetchedProduct = res.data?.product || {};

        // Check ownership: supplier must match
        const supplierId = fetchedProduct.supplier?._id || fetchedProduct.supplier;
        if (supplierId !== user?._id) {
          toast.error("You don't have permission to view this product");
          navigate("/seller/products");
          return;
        }

        setProduct(fetchedProduct);
        setSelectedImage(fetchedProduct.images?.[0] || "");
      } catch (err) {
        console.error("Error fetching product:", err);
        toast.error("Product not found");
        navigate("/seller/products");
      } finally {
        setLoading(false);
      }
    };
    if (id && user?._id) fetchProduct();
  }, [id, user?._id, navigate]);

  // Delete Product
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/products/delete-product/${product._id}`
      );
      setShowDeleteModal(false);
      Swal.fire({
        title: "Deleted!",
        text: `${getText(product?.name)} has been deleted successfully.`,
        icon: "success",
        confirmButtonColor: "#3085d6",
      });
      navigate("/seller/products");
    } catch (error) {
      console.error("Delete error:", error);
      Swal.fire({
        title: "Error",
        text: "Something went wrong while deleting the product.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <p className="text-gray-500 animate-pulse text-lg">Loading product...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50 text-gray-600">
        <p>Product not found</p>
        <button
          onClick={() => navigate("/seller/products")}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg"
        >
          Back to Products
        </button>
      </div>
    );
  }

  // Reusable Detail Card
  const DetailCard = ({ icon: Icon, label, value, colorClass = "text-gray-700" }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition">
      <div className="flex items-center gap-3 mb-2">
        <div className={`p-2 rounded-lg bg-opacity-10 ${colorClass}`}>
          <Icon className={`w-5 h-5 ${colorClass}`} />
        </div>
        <span className="text-sm text-gray-500 font-medium">{label}</span>
      </div>
      <p className={`text-lg font-semibold ${colorClass}`}>{value}</p>
    </div>
  );

  // Language Tabs
  const LanguageTabs = () => (
    <div className="flex space-x-2 mb-4">
      {["en", "hi", "es", "zh"].map((lang) => (
        <button
          key={lang}
          type="button"
          onClick={() => setActiveLanguage(lang)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeLanguage === lang
              ? "bg-blue-500 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {lang === "en" ? "English" : 
           lang === "hi" ? "हिंदी" : 
           lang === "es" ? "Español" : 
           "中文"}
        </button>
      ))}
    </div>
  );

  // Discount percentage
  const discountPercentage =
    product.previous_price && product.price
      ? (((product.previous_price - product.price) / product.previous_price) * 100).toFixed(1)
      : 0;

  return (
    <div className="min-h-screen bg-[#EEF3F7] p-2 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6">
        <div className="flex items-center gap-3 mb-4 md:mb-0">
          <button
            onClick={() => navigate("/seller/products")}
            className="p-2 bg-white border rounded-lg hover:bg-gray-100"
            title="Back"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-center gap-2">
            <Package className="text-primaryColor w-6 h-6" />
            <h1 className="text-2xl font-philosopher text-gray-800">Product Details</h1>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/seller/edit-product/${product._id}`)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
          >
            <Edit2 className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Language Selector */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Globe className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-800">View in Language</h3>
        </div>
        <LanguageTabs />
        <p className="text-sm text-gray-600 mt-2">
          Currently viewing in:{" "}
          <span className="font-semibold">
            {activeLanguage === "en" ? "English" : 
             activeLanguage === "hi" ? "Hindi" : 
             activeLanguage === "es" ? "Spanish" : 
             "Chinese"}
          </span>
        </p>
      </div>

      {/* Product Info */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Image */}
          <div className="md:col-span-1 flex flex-col items-center">
            <div className="bg-white border rounded-xl shadow-md flex items-center justify-center h-96 w-full overflow-hidden mb-4">
              <img
                src={selectedImage || product.images?.[0]}
                alt={getText(product?.name)}
                className="h-full w-auto object-contain"
              />
            </div>
            <div className="grid grid-cols-4 gap-3 w-full">
              {product.images?.map((img, i) => (
                <div
                  key={i}
                  onClick={() => setSelectedImage(img)}
                  className={`bg-white border rounded-lg h-20 flex items-center justify-center cursor-pointer overflow-hidden transition-all duration-200 hover:border-blue-500 ${
                    selectedImage === img
                      ? "border-blue-500 ring-2 ring-blue-200"
                      : "border-gray-200"
                  }`}
                >
                  <img src={img} alt={`thumb-${i}`} className="h-full w-auto object-contain" />
                </div>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="md:col-span-2 space-y-4">
            <h2 className="text-2xl font-semibold text-gray-900">{getText(product?.name)}</h2>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span className="font-semibold">SKU: {product.sku}</span>
              <span>|</span>
              <span>Category: {getEnglishText(product.category?.name) || "N/A"}</span>
            </div>

            <div className="flex items-center gap-3">
              <span
                className={`inline-block px-4 py-1 rounded-full text-sm font-medium ${
                  product.stock_status === "out of stock"
                    ? "bg-red-100 text-red-600"
                    : product.stock_status === "inactive"
                    ? "bg-gray-100 text-gray-600"
                    : "bg-green-100 text-green-600"
                }`}
              >
                {product.stock_status || "inactive"}
              </span>
              <span className="px-4 py-1 bg-blue-100 text-blue-600 rounded-full text-sm font-medium">
                {getEnglishText(product.subCategory?.name) || "—"}
              </span>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
              <p className="text-gray-600 whitespace-pre-wrap">
                {getText(product.description) || "No description provided."}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <DetailCard
          icon={DollarSign}
          label="Price"
          value={`₹${product.price || 0}`}
          colorClass="text-green-600"
        />
        <DetailCard
          icon={Box}
          label="Stock Status"
          value={product.stock_status || "inactive"}
          colorClass="text-blue-600"
        />
        <DetailCard
          icon={TrendingUp}
          label="Sold"
          value={product.sold || 0}
          colorClass="text-purple-600"
        />
        <DetailCard
          icon={Hash}
          label="Stock Qty"
          value={product.stock || 0}
          colorClass="text-orange-600"
        />
      </div>

      {/* Product Information & Inventory */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Information</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b pb-2">
              <span className="text-gray-600">Brand</span>
              <span className="font-medium text-gray-900">{product.brand || "N/A"}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="text-gray-600">Material</span>
              <span className="font-medium text-gray-900">{getText(product.material) || "—"}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="text-gray-600">Origin</span>
              <span className="font-medium text-gray-900">{getText(product.origin) || "—"}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="text-gray-600">Finish Type</span>
              <span className="font-medium text-gray-900">{getText(product.finishType) || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Product Type</span>
              <span className="font-medium text-gray-900">{getText(product.productType) || "—"}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Inventory & Shipping</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b pb-2">
              <span className="text-gray-600">Shipping Charge</span>
              <span className="font-medium text-gray-900">₹{product.shippingCharge || 0}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="text-gray-600">GST</span>
              <span className="font-medium text-gray-900">{product.gstPercentage || 0}%</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="text-gray-600">Height</span>
              <span className="font-medium text-gray-900">{product.height || "—"}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="text-gray-600">Weight</span>
              <span className="font-medium text-gray-900">{product.weight || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Warranty</span>
              <span className="font-medium text-gray-900">
                {product.warranty?.hasWarranty
                  ? `${product.warranty?.warrantyDays} days`
                  : "No warranty"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section (multilingual) */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {product.features && product.features.length > 0 ? (
            product.features.map((feature, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-xs font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-800 font-medium mb-1">{getText(feature)}</p>
                    {feature.en && (
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">EN:</span> {feature.en}
                      </div>
                    )}
                    {feature.hi && (
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">HI:</span> {feature.hi}
                      </div>
                    )}
                    {feature.es && (
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">ES:</span> {feature.es}
                      </div>
                    )}
                    {feature.zh && (
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">ZH:</span> {feature.zh}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-2 text-center text-gray-500 py-4">
              No features available for this product.
            </div>
          )}
        </div>
      </div>

      {/* Care Instructions & Return Policy */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Care Instructions</h3>
          <div className="text-sm text-gray-600 whitespace-pre-wrap">
            {getText(product.careInstruction) || "No care instructions provided."}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Return Policy</h3>
          <div className="text-sm text-gray-600 whitespace-pre-wrap">
            {getText(product.return_policy) || "No return policy specified."}
          </div>
        </div>
      </div>

      {/* Trade Information (if available) */}
      {product.tradeInfo && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Trade Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Payment Terms</h4>
              <p className="text-gray-600">{getText(product.tradeInfo.paymentTerms)}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Delivery Time</h4>
              <p className="text-gray-600">{getText(product.tradeInfo.deliveryTime)}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Supply Ability</h4>
              <p className="text-gray-600">{getText(product.tradeInfo.supplyAbility)}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Main Domestic Market</h4>
              <p className="text-gray-600">{getText(product.tradeInfo.mainDomesticMarket)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Product Video (if any) */}
      {product.productVideo && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Video</h3>
          <div className="aspect-video max-w-2xl mx-auto">
            <video
              src={product.productVideo}
              controls
              className="w-full h-full rounded-lg border border-gray-200"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Metadata</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Created At:</span>
            <span className="ml-2 font-medium text-gray-900">
              {new Date(product.createdAt).toLocaleDateString()}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Updated At:</span>
            <span className="ml-2 font-medium text-gray-900">
              {new Date(product.updatedAt).toLocaleDateString()}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Uploaded By:</span>
            <span className="ml-2 font-medium text-gray-900">
              {product.uploadedBy || "supplier"}
            </span>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteAlertModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        dltType="product"
        dltName={getText(product?.name)}
        onConfirm={handleDelete}
        dltLoading={deleteLoading}
      />
    </div>
  );
};

export default SupplierProductDetails;